import { useState } from "react";
import { BookOpen, Quote, ChevronDown } from "lucide-react";
import { PRAYER_CATEGORIES } from "../lib/prayers";

export default function Prayers() {
  const [activeId, setActiveId] = useState(PRAYER_CATEGORIES[0].id);
  const [openIndex, setOpenIndex] = useState(null);
  const active = PRAYER_CATEGORIES.find((c) => c.id === activeId);

  const switchTo = (id) => {
    setActiveId(id);
    setOpenIndex(null);
  };

  return (
    <div data-testid="prayers-page">
      {/* HERO */}
      <section className="relative bg-olive text-bone overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <p className="overline text-terracotta mb-4">Prayer</p>
          <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter leading-[0.95] max-w-3xl">
            Pray <em className="italic font-light text-terracotta">without</em> ceasing.
          </h1>
          <p className="mt-6 text-bone/80 text-lg max-w-2xl leading-relaxed">
            A living prayer library rooted in Scripture — for your morning quiet time,
            everyday declarations, and moments of spiritual battle.
          </p>
          <p className="mt-6 text-xs text-bone/60 italic max-w-2xl">
            Prayers inspired by <span className="text-terracotta font-semibold">Lionheart — Otha Turnbough</span>.
          </p>
        </div>
      </section>

      {/* TABS */}
      <section className="sticky top-[68px] z-30 bg-bone/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex gap-2 overflow-x-auto">
          {PRAYER_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => switchTo(cat.id)}
              data-testid={`prayer-tab-${cat.id}`}
              className={`px-5 py-2.5 text-xs sm:text-sm font-bold tracking-wider uppercase whitespace-nowrap transition-colors ${
                activeId === cat.id
                  ? "bg-olive text-bone"
                  : "bg-white border border-border text-ink/70 hover:border-olive"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </section>

      {/* PRAYER LIST */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <p className="overline text-terracotta mb-2">{active.label}</p>
        <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-olive mb-2">
          {active.tagline}
        </h2>
        <p className="text-sm text-muted_ink italic mb-10">
          Tap any prayer to expand the scripture and full text.
        </p>

        <div className="space-y-4">
          {active.prayers.map((p, i) => {
            const isOpen = openIndex === i;
            return (
              <article
                key={i}
                className={`bg-white border transition-colors ${
                  isOpen ? "border-olive" : "border-border"
                }`}
                data-testid={`prayer-item-${active.id}-${i}`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-start justify-between gap-4 p-6 text-left group"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen size={12} className="text-terracotta" />
                      {p.reference && (
                        <span className="text-[10px] font-bold text-terracotta tracking-widest uppercase">
                          {p.reference}
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading font-bold text-xl text-ink group-hover:text-olive transition-colors">
                      {p.heading}
                    </h3>
                  </div>
                  <ChevronDown
                    size={20}
                    className={`text-olive mt-1 shrink-0 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-6 -mt-2 space-y-5">
                    {p.scripture && (
                      <blockquote className="border-l-2 border-terracotta pl-4 py-1 text-ink/70 italic leading-relaxed relative">
                        <Quote size={14} className="absolute -left-2.5 -top-2 bg-white text-terracotta" />
                        {p.scripture}
                      </blockquote>
                    )}
                    <div>
                      <p className="overline text-olive mb-2">Prayer</p>
                      <p className="text-ink/85 leading-relaxed whitespace-pre-line">{p.body}</p>
                    </div>
                    <p className="text-terracotta font-heading font-bold text-sm tracking-wide">
                      Amen.
                    </p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
